import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type UserRole = "pet_owner" | "vet" | "admin" | null;

// Hard ceiling for the entire resolution attempt (retries included). After
// this elapses we settle the hook with whatever we have so consumers can
// render a fallback instead of an infinite skeleton.
const RESOLUTION_TIMEOUT_MS = 5000;
const MAX_ATTEMPTS = 6;
const RETRY_DELAY_MS = 300;

type Telemetry = {
  event: "resolved" | "timeout" | "error";
  userId: string;
  attempts: number;
  durationMs: number;
  role: UserRole;
  error?: string;
};

const emitTelemetry = (t: Telemetry) => {
  // Console is the universal sink; analytics layers can subscribe via the
  // window event without coupling this hook to a specific provider.
  // eslint-disable-next-line no-console
  console.info("[useUserRole]", t);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("useUserRole:telemetry", { detail: t }));
  }
};

// Module-level cache so concurrent consumers share one network round-trip
// per user id. Without this, every component that calls useUserRole fires
// its own `user_roles` query on mount (we saw 5x duplicates post-login).
type CacheEntry = {
  promise: Promise<UserRole>;
  role: UserRole;
  resolved: boolean;
  attempts: number;
  startedAt: number;
};
const roleCache = new Map<string, CacheEntry>();

const fetchRoleOnce = (userId: string): CacheEntry => {
  const existing = roleCache.get(userId);
  if (existing) return existing;
  const startedAt = Date.now();
  const entry: CacheEntry = {
    role: null,
    resolved: false,
    attempts: 0,
    startedAt,
    // Placeholder; assigned below once `entry` is in scope so the async
    // body can safely reference it (avoids a TDZ on entry.attempts).
    promise: Promise.resolve(null),
  };
  entry.promise = (async () => {
    try {
      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        entry.attempts = attempt + 1;
        const { data, error } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", userId);
        if (error) {
          emitTelemetry({
            event: "error",
            userId,
            attempts: entry.attempts,
            durationMs: Date.now() - startedAt,
            role: null,
            error: error.message,
          });
          // Don't cache transient backend failures (e.g. PostgREST 503/504
          // schema-cache outages). Otherwise admins/vets are permanently
          // downgraded to pet-owner routing until a hard reload. Retry, and
          // on final failure evict the entry so the next consumer retries.
          if (attempt === MAX_ATTEMPTS - 1) {
            entry.role = null;
            entry.resolved = true;
            roleCache.delete(userId);
            return null;
          }
          await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
          continue;
        }
        const roles = (data ?? []).map((r) => r.role as Exclude<UserRole, null>);
        const resolved: UserRole = roles.includes("admin")
          ? "admin"
          : roles.includes("vet")
            ? "vet"
            : roles.includes("pet_owner")
              ? "pet_owner"
              : null;
        if (resolved !== null || attempt === MAX_ATTEMPTS - 1) {
          entry.role = resolved;
          entry.resolved = true;
          emitTelemetry({
            event: "resolved",
            userId,
            attempts: entry.attempts,
            durationMs: Date.now() - startedAt,
            role: resolved,
          });
          // Don't cache a definitive "no role" result either - the row may
          // appear after a slow role assignment finishes.
          if (resolved === null) roleCache.delete(userId);
          return resolved;
        }
        await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
      }
      entry.resolved = true;
      if (entry.role === null) roleCache.delete(userId);
      return entry.role;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      emitTelemetry({
        event: "error",
        userId,
        attempts: entry.attempts,
        durationMs: Date.now() - startedAt,
        role: null,
        error: message,
      });
      entry.role = null;
      entry.resolved = true;
      roleCache.delete(userId);
      return null;
    }
  })();
  roleCache.set(userId, entry);
  return entry;
};


export const clearUserRoleCache = (userId?: string) => {
  if (userId) roleCache.delete(userId);
  else roleCache.clear();
};

export const useUserRole = () => {
  const { user, onboardingLoading } = useAuth();
  const [role, setRole] = useState<UserRole>(null);
  const [resolvedFor, setResolvedFor] = useState<string | null | undefined>(undefined);
  const [timedOut, setTimedOut] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);

  const currentUserId = user?.id ?? null;

  useEffect(() => {
    if (!currentUserId) {
      setRole(null);
      setResolvedFor(null);
      setTimedOut(false);
      return;
    }

    let cancelled = false;
    setTimedOut(false);
    const entry = fetchRoleOnce(currentUserId);

    if (entry.resolved) {
      setRole(entry.role);
      setResolvedFor(currentUserId);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      if (cancelled) return;
      roleCache.delete(currentUserId);
      setTimedOut(true);
      emitTelemetry({
        event: "timeout",
        userId: currentUserId,
        attempts: entry.attempts,
        durationMs: Date.now() - entry.startedAt,
        role: null,
      });
      setRetryNonce((n) => n + 1);
    }, RESOLUTION_TIMEOUT_MS);

    entry.promise.then((resolved) => {
      if (cancelled) return;
      setRole(resolved);
      setResolvedFor(currentUserId);
      setTimedOut(false);
      window.clearTimeout(timeoutId);
    });

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [currentUserId, retryNonce]);

  const loading = onboardingLoading || resolvedFor !== currentUserId;

  return { role, loading, timedOut };
};
